import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgLabelTemplateDirective, NgNotFoundTemplateDirective, NgOptionTemplateDirective, NgSelectComponent } from '@ng-select/ng-select';
import { Store } from '@ngrx/store';
import { uniqueId } from 'lodash-es';
import { BehaviorSubject, Observable, Subject, catchError, combineLatest, debounceTime, map, of, startWith, switchMap, tap } from 'rxjs';

import { DropdownItem, User } from '@shared/models';
import { TeamMemberService } from '@shared/services';
import { fromAuth } from '@shared/store';

@Component({
  selector: 'app-assignee-select',
  templateUrl: 'assignee-select.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgSelectComponent,
    FormsModule,
    NgLabelTemplateDirective,
    NgOptionTemplateDirective,
    NgTemplateOutlet,
    NgNotFoundTemplateDirective,
    AsyncPipe,
  ],
})
export class AssigneeSelectComponent implements OnChanges {
  @ViewChild('select') selectRef!: NgSelectComponent;

  store = inject(Store);

  @Input() label!: string;

  @Input() required = false;

  @Input() selectedAssignee?: DropdownItem;

  @Input() selectedItem?: string;

  @Input() items$!: Observable<DropdownItem[]>;

  @Input() loading = false;

  @Input() disabled = false;

  @Input() placeholder = '';

  @Output() changed = new EventEmitter<DropdownItem | undefined>();

  @Output() filterOpened = new EventEmitter();

  @Output() closed = new EventEmitter();

  activeUser$ = this.store.select(fromAuth.selectUser);

  assigneeInput$ = new Subject<string>();

  selectedAssignee$ = new BehaviorSubject<DropdownItem | undefined>(undefined);

  selectId = uniqueId();

  teamMemberService = inject(TeamMemberService);

  selectedItemOnOpen?: DropdownItem;

  getDefaultItems(user: User | null, selectedAssignee?: DropdownItem) {
    return [
      ...(selectedAssignee && ![user?.id, 'UNASSIGNED'].includes(selectedAssignee.value) ? [selectedAssignee] : []),
      { label: `${user?.name ?? ''}::${user?.email ?? ''}`, value: user?.id ?? '' },
      { label: 'Unassigned', value: 'UNASSIGNED' },
    ];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedAssignee']) {
      this.selectedAssignee$.next(this.selectedAssignee);
    }
  }

  constructor() {
    this.items$ = combineLatest([this.activeUser$, this.selectedAssignee$]).pipe(
      switchMap(([user, selectedAssignee]) => {
        return this.assigneeInput$.pipe(
          startWith(''),
          debounceTime(300),
          switchMap((term) => {
            if (!term) {
              return of(this.getDefaultItems(user, selectedAssignee));
            } else {
              this.loading = true;
              return this.teamMemberService.getBusinessAccountTeamMembers({ searchString: term }).pipe(
                tap({ next: () => (this.loading = false) }),
                map((items) => {
                  return [
                    ...items.items.map((employee) => {
                      return { label: `${employee.displayName}::${employee.primaryEmail}`, value: employee.id };
                    }),
                    {
                      label: 'Unassigned',
                      value: 'UNASSIGNED',
                    },
                  ];
                }),
                catchError(() => of([] as DropdownItem[])),
              );
            }
          }),
        );
      }),
    );
  }

  onChange(item: DropdownItem): void {
    this.changed.emit(item);
  }

  onOpen() {
    this.selectedItem = this.selectedAssignee?.value ?? 'UNASSIGNED';

    this.filterOpened.emit();
  }

  onClose() {
    this.assigneeInput$.next('');

    if (this.selectedItemOnOpen?.value !== this.selectedItem) {
      this.closed.emit();
    }

    this.selectRef?.blur();
  }
}
